import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { LogoMark } from "@/components/Brand";
import { PolicyContentView } from "@/components/PolicyContentView";
import { CheckCircle2, Loader2, ShieldCheck } from "lucide-react";

export default function SignPolicy() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ name: "", role: "", confirmed: false });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    api.get(`/sign/${token}`).then((r) => setData(r.data)).catch((e) => setError(formatApiError(e.response?.data?.detail) || "This link is not valid"));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { const { data: r } = await api.post(`/sign/${token}`, form); setDone(r.signed_at); }
    catch (err) { setError(formatApiError(err.response?.data?.detail) || "Could not record your signature"); }
    finally { setBusy(false); }
  };

  if (error) return <div className="max-w-xl mx-auto px-4 py-24 text-center" data-testid="sign-error"><LogoMark className="mx-auto mb-6" /><p className="text-destructive">{error}</p></div>;
  if (!data) return <div className="max-w-xl mx-auto px-4 py-24 text-center text-sm text-muted-foreground">Loading policy…</div>;
  const signed = done || data.status === "signed";
  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10" data-testid="sign-page">
      <div className="flex items-center gap-3 mb-6"><LogoMark /><span className="text-xs uppercase tracking-widest text-muted-foreground">Policy sign-off</span></div>
      <div className="bg-card border border-border rounded-2xl p-6 mb-6">
        <p className="text-sm text-muted-foreground">{data.org_name || data.requester} · {data.sector_label}</p>
        <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight mt-1" data-testid="sign-title">{data.title}</h1>
        {data.message && <p className="mt-3 text-sm rounded-lg bg-muted/50 p-3" data-testid="sign-message">{data.message}</p>}
        <p className="mt-3 text-sm">Please read the policy below in full, then confirm at the bottom that you have read and understood it. Your confirmation is recorded against {data.email}.</p>
      </div>
      <div className="bg-card border border-border rounded-2xl p-6 mb-6"><PolicyContentView content={data.content} testId="sign-policy-content" /></div>
      <div className="bg-card border border-border rounded-2xl p-6" data-testid="sign-form-card">
        {signed ? (
          <div className="flex items-center gap-3 text-emerald-700" data-testid="sign-success">
            <CheckCircle2 className="w-6 h-6" />
            <div><p className="font-semibold">Thank you — your sign-off is recorded</p><p className="text-sm text-muted-foreground">{data.signed_name || form.name} · {new Date(done || data.signed_at).toLocaleString("en-GB")}</p></div>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3 max-w-md">
            <p className="font-semibold flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-accent" /> Confirm you have read this policy</p>
            <Input placeholder="Your full name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="sign-name" />
            <Input placeholder="Your role (optional)" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="sign-role" />
            <label className="flex items-start gap-2 text-sm cursor-pointer">
              <Checkbox checked={form.confirmed} onCheckedChange={(c) => setForm({ ...form, confirmed: !!c })} data-testid="sign-confirm" className="mt-0.5" />
              I confirm I have read and understood this policy and will follow it in my work.
            </label>
            <Button type="submit" disabled={busy || !form.confirmed || !form.name} data-testid="sign-submit">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign and confirm"}</Button>
          </form>
        )}
      </div>
    </div>
  );
}
